import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  NotificationSettingsForm,
  StyledButtonContainer,
  NotificationSettingsList,
  SaveButton,
  CancelButton,
  GroupedNotificationItem,
  NotificationGroupHeader,
  NotificationGroupContent,
  StyledTitleTypography,
  StyledDescriptionTypography,
  StyledButtonTypography,
  StyledSectionHeaderTypography,
} from './UserNotificationSettings.styles';
import StyledSwitch from '../../../../StyledSwitch/StyledSwitch';
import { selectNotificationSettings } from '../../../../../redux/people/peopleSlice';
import { fetchNotificationSettings, saveNotificationSettings } from '../../../../../redux/people/peopleThunks';
import { selectHasPrivilegedRole } from '../../../../../redux/slices/currentUserSlice';
import {
  GROUPED_NOTIFICATION_SETTINGS,
  convertToGroupedSettings,
  convertToIndividualSettings,
} from '../../../../../utils/constants/groupedNotifications';
import { showSuccessToast, showErrorToast } from '../../../../../utils/toastUtils';

export default function UserNotificationSettings({ employee, handleOpenDrawer }) {
  const dispatch = useDispatch();
  const notifications = useSelector(selectNotificationSettings);
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);

  const [groupedSettings, setGroupedSettings] = useState(() => {
    // Initialize with all groups enabled by default
    const defaultSettings = {};
    GROUPED_NOTIFICATION_SETTINGS.forEach((group) => {
      defaultSettings[group.id] = true;
    });
    return defaultSettings;
  });

  useEffect(() => {
    dispatch(fetchNotificationSettings(employee.id));
  }, [dispatch, employee.id]);

  useEffect(() => {
    if (notifications) {
      // Convert individual notification settings to grouped settings
      const converted = convertToGroupedSettings(notifications);
      setGroupedSettings(converted);
    }
  }, [notifications]);

  const handleChange = (groupId) => {
    setGroupedSettings((prevSettings) => ({
      ...prevSettings,
      [groupId]: !prevSettings[groupId],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleOpenDrawer('user', employee);
    handleSaveSettings();
  };

  const handleSaveSettings = async () => {
    // Convert grouped settings back to individual settings for the API
    const individualSettings = convertToIndividualSettings(groupedSettings);
    try {
      await dispatch(saveNotificationSettings({ eid: employee.id, payload: individualSettings })).unwrap();
      showSuccessToast('Saved notification settings');
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      showErrorToast('Failed to save notification settings');
    }
  };

  // Filter notification groups based on user role
  const employeeNotificationGroups = GROUPED_NOTIFICATION_SETTINGS.filter((group) => {
    // Show non-privileged groups to all users
    return !group.isPrivileged;
  });

  const managerNotificationGroups = GROUPED_NOTIFICATION_SETTINGS.filter((group) => {
    // Show privileged groups only to privileged users
    return group.isPrivileged && hasPrivilegedRole;
  });

  return (
    <NotificationSettingsForm onSubmit={handleSubmit}>
      <NotificationSettingsList>
        {hasPrivilegedRole && <StyledSectionHeaderTypography>Employee notifications</StyledSectionHeaderTypography>}
        {employeeNotificationGroups.map((group) => (
          <GroupedNotificationItem key={group.id}>
            <NotificationGroupHeader>
              <NotificationGroupContent>
                <StyledTitleTypography>{group.title}</StyledTitleTypography>
                <StyledDescriptionTypography>{group.description}</StyledDescriptionTypography>
              </NotificationGroupContent>
              <StyledSwitch
                checked={groupedSettings[group.id]}
                onChange={() => handleChange(group.id)}
                ariaLabel={`Toggle ${group.title}`}
              />
            </NotificationGroupHeader>
          </GroupedNotificationItem>
        ))}
        {hasPrivilegedRole && managerNotificationGroups.length > 0 && (
          <>
            <StyledSectionHeaderTypography>Manager notifications</StyledSectionHeaderTypography>
            {managerNotificationGroups.map((group) => (
              <GroupedNotificationItem key={group.id}>
                <NotificationGroupHeader>
                  <NotificationGroupContent>
                    <StyledTitleTypography>{group.title}</StyledTitleTypography>
                    <StyledDescriptionTypography>{group.description}</StyledDescriptionTypography>
                  </NotificationGroupContent>
                  <StyledSwitch
                    checked={groupedSettings[group.id]}
                    onChange={() => handleChange(group.id)}
                    ariaLabel={`Toggle ${group.title}`}
                  />
                </NotificationGroupHeader>
              </GroupedNotificationItem>
            ))}
          </>
        )}
      </NotificationSettingsList>
      <StyledButtonContainer>
        <SaveButton type="submit">
          <StyledButtonTypography>Save</StyledButtonTypography>
        </SaveButton>
        <CancelButton type="button" onClick={() => handleOpenDrawer('user', employee)}>
          <StyledButtonTypography>Cancel</StyledButtonTypography>
        </CancelButton>
      </StyledButtonContainer>
    </NotificationSettingsForm>
  );
}

UserNotificationSettings.propTypes = {
  employee: PropTypes.object.isRequired,
  handleOpenDrawer: PropTypes.func.isRequired,
};
